import {useNavigate, useParams} from "react-router-dom";
import SectionHeader from "../../../../util/component/SectionHeader";
import {useContext, useEffect, useRef, useState} from "react";
import {
    Button,
    Chip,
    FormControlLabel,
    Grid,
    OutlinedInput,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography
} from "@mui/material";
import {useCouponActions} from "../../service/useCouponActions";
import {useTranslation} from "react-i18next";
import {useProductCodeActions} from "../../service/useProductCodeActions";
import dayjs from "dayjs";
import CouponCampaignAddCodesModal from "./modals/CouponCampaignAddCodesModal";
import {ColDef, GetRowIdParams, GridOptions, IServerSideGetRowsParams} from "ag-grid-enterprise";
import {AgGridReact} from "ag-grid-react";
import {ArrowBackRounded, LibraryAddRounded, SearchRounded} from "@mui/icons-material";
import {Box} from "@mui/system";
import {
    COUPON_CODE_STATUS_DELIVERED,
    COUPON_CODE_STATUS_NOT_USED,
    COUPON_CODE_STATUS_USED
} from "../../component/Constants";
import CouponCodeUpdateModal from "./modals/CouponCodeUpdateModal";
import {useStatusValuesCoupon} from "../../component/useStatusValuesCoupon";
import AgGridCustomTable from "../../../../util/component/AgGridCustomTable";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {setSnackbar} from "../../../../util/state/globalAction";
import {snackbarTypes} from "../../../../util/state/globalVariables";
import {CouponFiltersCampaignCodes, CouponInputByCampaignGetAll} from "./interfaces/CouponInterfaces";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import ActionButtonWithTooltip, {ActionTypes} from "../../component/ActionButtonWithTooltip";
import {useHasScope} from "../../../../config/globalConfig";

const CouponCampaignCodesPage = () => {
    const {t} = useTranslation(['salesmarketing', 'translation']);
    const {couponCampaignId} = useParams();
    const {getCouponCampaignById, getCouponList} = useCouponActions();
    const {findProductCode} = useProductCodeActions();
    const {getStatusColor, getStatusLabel} = useStatusValuesCoupon();
    const {exportCouponList} = useCouponActions();
    const {globalData, dispatchGlobalData} = useContext(GlobalContext);
    const [searchInput, setSearchInput] = useState("");
    const [statusList, setStatusList] = useState<any[]>([]);
    const gridRef = useRef<AgGridReact>(null);
    const [couponCampaign, setCouponCampaign] = useState<any>(null);
    const [selectedProduct, setSelectedProduct] = useState<any>(null)
    const [openAddCodesModal, setOpenAddCodesModal] = useState<any>(false)
    const [openUpdateCodeModal, setOpenUpdateCodeModal] = useState<any>(false)
    const [selectedCouponCode, setSelectedCouponCode] = useState<any>(null)
    const [countNotUsed, setCountNotUsed] = useState(0);
    const [countUsed, setCountUsed] = useState(0);
    const [countDelivered, setCountDelivered] = useState(0);
    const navigate = useNavigate();
    const hasScopes = useHasScope;

    const columns: ColDef[] = [
        {
            field: "couponCode",
            headerName: t('coupon.listColumns.couponCode'),
            unSortIcon: true
        },
        {
            field: "note",
            headerName: t('coupon.listColumns.note'),
            unSortIcon: true,
            tooltipField: "note"
        },
        {
            field: "bookingCode",
            headerName: t('coupon.listColumns.bookingCode'),
            unSortIcon: true
        },
        {
            field: "createdDate",
            headerName: t('coupon.listColumns.creationDate'),
            unSortIcon: true,
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
        },
        {
            field: "usageDate",
            headerName: t('coupon.listColumns.usageDate'),
            unSortIcon: true,
            cellRenderer: (params: any) => params.value ? dayjs(params.value).format("L") : "",
        },
        {
            field: "status",
            headerName: t('coupon.listColumns.status'),
            unSortIcon: true,
            cellRenderer: (params: any) => {
                return <Chip label={getStatusLabel(params.data.status)}
                             sx={{backgroundColor: getStatusColor(params.data.status)}}/>
            }
        },
        {
            field: "_actions",
            headerName: t('coupon.listColumns.action'),
            pinned: 'right',
            lockPosition: 'right',
            lockPinned: true,
            sortable: false,
            suppressSizeToFit: true,
            cellRenderer: (params: any) => {
                return (
                    <ActionButtonWithTooltip auth={'SCOPE_SM_UPDATE_COUPON'} type={ActionTypes.edit} callback={() => {
                        setSelectedCouponCode(params.data);
                        setOpenUpdateCodeModal(true);
                    }}/>
                );
            }
        }
    ];

    useEffect(() => {
        if (couponCampaignId) {
            getCouponCampaignById(couponCampaignId).then(res => {
                setCouponCampaign(res);
                findProductCode(res.productCode).then(response => {
                    setSelectedProduct(response);
                })
            })
        }
    }, []);

    useEffect(() => {
        gridRef?.current?.api?.setServerSideDatasource(createDatasource());
    }, [statusList, searchInput]);

    function updateCounter(data: any) {
        setCountUsed(0);
        setCountNotUsed(0);
        setCountDelivered(0);
        if (data && data.length > 0) {
            data.map((d: any) => {
                switch (d.status) {
                    case COUPON_CODE_STATUS_USED:
                        setCountUsed(d.count);
                        break;
                    case COUPON_CODE_STATUS_NOT_USED:
                        setCountNotUsed(d.count);
                        break;
                    case COUPON_CODE_STATUS_DELIVERED:
                        setCountDelivered(d.count);
                        break;
                }
            })
        }
    }

    function createDatasource() {
        return {
            getRows: async (params: IServerSideGetRowsParams) => {
                let filterTemp: CouponInputByCampaignGetAll = {} as CouponInputByCampaignGetAll;
                filterTemp.offset = params.request.startRow ? params.request.startRow : 0;
                filterTemp.limit = (params.request.endRow ? params.request.endRow : 10) - filterTemp.offset;
                filterTemp.sort = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].colId;
                filterTemp.order = params.request.sortModel.length === 0 ? null : params.request.sortModel[0].sort;
                filterTemp.search = searchInput;
                filterTemp.status = statusList;
                filterTemp.id = couponCampaignId;
                let data = await getCouponList(filterTemp);
                updateCounter(data.countByStatus);
                params.success({
                    rowData: data?.content,
                    rowCount: data?.totalElements
                });
            }
        };
    };

    const onDownloadCouponList = async () => {
        try {
            const filters: CouponFiltersCampaignCodes = {
                id: couponCampaignId,
                status: statusList,
                dealerId: globalData?.user?.dealers?.map((d: any) => d.name),
                tenantId: globalData?.user?.legalEntities?.map((d: any) => d.name)
            }
            const {result} = await exportCouponList(filters);
            var mediaType = "data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,";
            // create "a" HTML element with href to file & click
            const link = document.createElement('a');
            link.href = mediaType + result.filecontent;
            console.warn(link.href)
            link.setAttribute('download', result.filename);
            document.body.appendChild(link);
            link.click();

            // clean up "a" element & remove ObjectURL
            document.body.removeChild(link);
        } catch (err) {
            dispatchGlobalData({
                type: setSnackbar,
                payload: {
                    snackbar: {
                        message: t("downloadError"),
                        type: snackbarTypes.error,
                        show: true,
                        autoClose: true,
                    }
                }
            });
        }
    }

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        suppressRowClickSelection: true,
        domLayout: 'autoHeight',
        rowModelType: 'serverSide',
        defaultColDef: {
            sortable: true,
        },
        columnDefs: columns,
        onGridReady: (params) => {
            params.api.sizeColumnsToFit();
            params.api.setServerSideDatasource(createDatasource());
        },
        serverSideDatasource: createDatasource(),
    };

    return (
        <>
            <SectionHeader title={t('coupon.listHeader.title')}
                           subtitle={t('coupon.listHeader.subTitle')}/>
            <Paper elevation={3} sx={{padding: "16px", margin: "18px"}}>
                <Button size={"medium"} variant="text" startIcon={<ArrowBackRounded/>}
                        onClick={() => navigate("/salesmarketing/couponcampaignlist")}>
                    {t('coupon.couponCampaignList')}
                </Button>
                {couponCampaign && selectedProduct &&
                    <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "25px"}}>
                        <Grid item xs={3}>
                            <TextField
                                margin="dense"
                                id="status"
                                label={t('coupon.productCode')}
                                fullWidth
                                variant="standard"
                                value={couponCampaign.productCode}
                                inputProps={
                                    {readOnly: true}
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                fullWidth
                                name="selectedProductName"
                                label={t('coupon.selectedProductName')}
                                value={selectedProduct.name}
                                inputProps={
                                    {readOnly: true}
                                }
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField
                                fullWidth
                                name="selectedProductDescription"
                                label={t('coupon.selectedProductDescription')}
                                value={selectedProduct.description}
                                inputProps={
                                    {readOnly: true}
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                margin="dense"
                                id="status"
                                label={t('coupon.bookingStartDate')}
                                fullWidth
                                variant="standard"
                                value={couponCampaign.bookingStartDate ? dayjs(couponCampaign.bookingStartDate).format("L") : null}
                                inputProps={
                                    {readOnly: true}
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                margin="dense"
                                id="status"
                                label={t('coupon.bookingEndDate')}
                                fullWidth
                                variant="standard"
                                value={couponCampaign.bookingEndDate ? dayjs(couponCampaign.bookingEndDate).format("L") : null}
                                inputProps={
                                    {readOnly: true}
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                margin="dense"
                                id="status"
                                label={t('coupon.pickupStartDate')}
                                fullWidth
                                variant="standard"
                                value={couponCampaign.pickupStartDate ? dayjs(couponCampaign.pickupStartDate).format("L") : null}
                                inputProps={
                                    {readOnly: true}
                                }
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <TextField
                                margin="dense"
                                id="status"
                                label={t('coupon.pickupEndDate')}
                                fullWidth
                                variant="standard"
                                value={couponCampaign.pickupEndDate ? dayjs(couponCampaign.pickupEndDate).format("L") : null}
                                inputProps={
                                    {readOnly: true}
                                }
                            />
                        </Grid>
                    </Grid>}
                {
                    hasScopes('SCOPE_SM_CREATE_COUPON') ?
                        <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                            <Grid item xs={12}>
                                <Button
                                    sx={{float: "right"}} variant={"text"} color={"accent"}
                                    endIcon={<LibraryAddRounded/>} onClick={() => setOpenAddCodesModal(true)}>
                                    {t('coupon.addCodes.addNewButton')}
                                </Button>
                            </Grid>
                        </Grid> :
                        <></>
                }
                <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "25px"}} alignItems={"center"}>
                    <Grid item xs={3}>
                        <Stack direction="row" columnGap={1}>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: '5px'}}>
                                <Typography variant="subtitle2" fontSize=".9rem"
                                            color={'primary'}>{t('coupon.statusValues.notUsed')}: <strong>{countNotUsed}</strong></Typography>
                                |
                                <Typography variant="subtitle2" fontSize=".9rem"
                                            color={'primary'}>{t('coupon.statusValues.delivered')}: <strong>{countDelivered}</strong></Typography>
                                |
                                <Typography variant="subtitle2" fontSize=".9rem"
                                            color={'primary'}>{t('coupon.statusValues.used')}: <strong>{countUsed}</strong></Typography>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item xs={6} container justifyContent={"center"}>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Switch sx={{margin: "5px"}}
                                                 checked={statusList.some(s => s === COUPON_CODE_STATUS_NOT_USED)}
                                                 onChange={(e) => {
                                                     if (e.target.checked) {
                                                         setStatusList([...statusList, COUPON_CODE_STATUS_NOT_USED]);
                                                     } else {
                                                         setStatusList([...statusList].filter(s => s !== COUPON_CODE_STATUS_NOT_USED));
                                                     }
                                                 }}/>}
                                label={t('coupon.statusValues.notUsed')}
                                labelPlacement={"start"}/>
                        </Grid>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Switch sx={{margin: "5px"}}
                                                 checked={statusList.some(s => s === COUPON_CODE_STATUS_DELIVERED)}
                                                 onChange={(e) => {
                                                     if (e.target.checked) {
                                                         setStatusList([...statusList, COUPON_CODE_STATUS_DELIVERED]);
                                                     } else {
                                                         setStatusList([...statusList].filter(s => s !== COUPON_CODE_STATUS_DELIVERED));
                                                     }
                                                 }}/>}
                                label={t('coupon.statusValues.delivered')}
                                labelPlacement={"start"}/>
                        </Grid>
                        <Grid item xs={3}>
                            <FormControlLabel
                                control={<Switch sx={{margin: "5px"}}
                                                 checked={statusList.some(s => s === COUPON_CODE_STATUS_USED)}
                                                 onChange={(e) => {
                                                     if (e.target.checked) {
                                                         setStatusList([...statusList, COUPON_CODE_STATUS_USED]);
                                                     } else {
                                                         setStatusList([...statusList].filter(s => s !== COUPON_CODE_STATUS_USED));
                                                     }
                                                 }}/>}
                                label={t('coupon.statusValues.used')}
                                labelPlacement={"start"}/>
                        </Grid>
                    </Grid>
                    <Grid item xs={3}>
                        <OutlinedInput
                            fullWidth
                            inputProps={{maxLength: 120}}
                            value={searchInput}
                            onChange={(event) => {
                                setSearchInput(event?.target.value)
                            }}
                            size="small"
                            startAdornment={<SearchRounded/>}
                        />
                    </Grid>
                    {
                        hasScopes('SCOPE_SM_EXPORT_COUPON') ?
                            <Grid item xs={12}>
                                <Button size="small" style={{whiteSpace: "nowrap", float: "right"}}
                                        onClick={onDownloadCouponList} startIcon={<FileUploadIcon/>}>
                                    {t('coupon.buttons.exportCouponsBtnText')}
                                </Button>
                            </Grid>
                            :<></>
                    }
                </Grid>
                <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                    <Grid item xs={12}>
                        <AgGridCustomTable gridRef={gridRef} gridOptions={gridOptions}/>
                    </Grid>
                </Grid>
                {openAddCodesModal &&
                    <CouponCampaignAddCodesModal couponCampaignId={couponCampaignId} open={openAddCodesModal}
                                                 handleClose={(reload: any) => {
                                                     setOpenAddCodesModal(false);
                                                     if (reload) {
                                                         gridRef?.current!.api?.setServerSideDatasource(createDatasource());
                                                     }
                                                 }}/>}
                {openUpdateCodeModal &&
                    <CouponCodeUpdateModal open={openUpdateCodeModal} handleClose={(reload: any) => {
                        setOpenUpdateCodeModal(false);
                        if (reload) {
                            gridRef?.current!.api?.setServerSideDatasource(createDatasource());
                        }
                    }} selectedCouponCode={selectedCouponCode}/>}

            </Paper>
        </>
    )
}

export default CouponCampaignCodesPage;